// Compliance, Fraud & Risk Center — Polished UI
// Additions: overall risk gauge, severity donut, filing deadline timeline,
// bulk remediation actions, summary strip.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider,
    FormControlLabel, Switch, IconButton, Tooltip,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, SimpleBarChart, DonutChart, RadialGauge, SeverityBadge,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    fraudCompositeScan, fraudGhostVendors, controlsSegregation,
    controlsPolicyViolations, regFilingDeadlines, regComplianceGaps,
    complianceFraudRiskSummary,
} from './api';

const TABS = ['Composite Risk Scan', 'Ghost Vendors', 'Segregation of Duties', 'Policy Violations', 'Filing Deadlines', 'Compliance Gaps'];

const riskToNumber = (r) => r === 'CRITICAL' ? 95 : r === 'HIGH' ? 75 : r === 'MEDIUM' ? 50 : r === 'LOW' ? 20 : 10;
const riskColor = (r) => r === 'CRITICAL' || r === 'HIGH' ? CHART_COLORS.danger : r === 'MEDIUM' ? CHART_COLORS.warning : CHART_COLORS.success;

export default function FraudDetectionAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [useAi, setUseAi] = useState(true);
    const [summary, setSummary] = useState(null);

    const [scanResult, setScanResult] = useState(null);
    const [ghostVendors, setGhostVendors] = useState(null);
    const [segregation, setSegregation] = useState(null);
    const [violations, setViolations] = useState(null);
    const [deadlines, setDeadlines] = useState(null);
    const [gaps, setGaps] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        complianceFraudRiskSummary().then(r => r?.data?.success && setSummary(r.data.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const load = async () => {
            if (tab === 1 && !ghostVendors) { const r = await run(() => fraudGhostVendors()); if (r?.data?.success) setGhostVendors(r.data.data); }
            else if (tab === 2 && !segregation) { const r = await run(() => controlsSegregation()); if (r?.data?.success) setSegregation(r.data.data); }
            else if (tab === 3 && !violations) { const r = await run(() => controlsPolicyViolations()); if (r?.data?.success) setViolations(r.data.data); }
            else if (tab === 4 && !deadlines) { const r = await run(() => regFilingDeadlines()); if (r?.data?.success) setDeadlines(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'Overall Risk', value: scanResult?.overall_risk_level || summary?.risk?.overall_risk || '—', accent: riskColor(scanResult?.overall_risk_level) === CHART_COLORS.danger ? 'danger' : 'warning' },
        { label: 'Ghost Vendor Flags', value: (ghostVendors?.flagged_vendors?.length) ?? (scanResult?.ghost_vendors?.flagged_vendors?.length) ?? '—', accent: 'warning' },
        { label: 'Policy Violations', value: violations?.total_violations ?? '—', accent: (violations?.total_violations || 0) > 0 ? 'danger' : 'success' },
        { label: 'Overdue Filings', value: deadlines?.overdue_count ?? (deadlines?.overdue || []).length ?? '—', accent: 'danger' },
    ];

    // Risk signal distribution
    const riskSignals = (() => {
        if (!scanResult) return [];
        return [
            { name: 'Ghost Vendors', value: scanResult.ghost_vendors?.flagged_vendors?.length || 0 },
            { name: 'Split Transactions', value: scanResult.split_transactions?.splits?.length || 0 },
            { name: 'Benford Anomaly', value: scanResult.benfords?.conforms === false ? 1 : 0 },
            { name: 'Duplicate Payments', value: scanResult.duplicate_payments?.count || 0 },
        ].filter(d => d.value > 0);
    })();

    // Severity distribution for violations
    const violationSeverityData = (() => {
        if (!violations?.violations) return [];
        const counts = violations.violations.reduce((acc, v) => {
            const key = v.severity || 'UNKNOWN';
            acc[key] = (acc[key] || 0) + (v.count || 1);
            return acc;
        }, {});
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    })();

    const allDeadlines = deadlines
        ? (deadlines.deadlines || []).concat(deadlines.federal_deadlines || []).concat(deadlines.state_deadlines || [])
        : [];

    const renderTab = () => {
        switch (tab) {
            case 0:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Composite Fraud & Risk Scan"
                            subtitle="Unified scan across ghost vendors, split transactions, duplicates, Benford's Law"
                            icon={<SecurityIcon color="error" />}
                            actions={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <FormControlLabel
                                        control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                        label={<Typography variant="caption">AI Analysis</Typography>}
                                    />
                                    <Button variant="contained" size="small" color="error" startIcon={<PlayArrowIcon />}
                                        onClick={async () => {
                                            const r = await run(() => fraudCompositeScan(useAi));
                                            if (r?.data?.success) setScanResult(r.data.data);
                                        }}>Run Scan</Button>
                                </Stack>
                            }
                        />
                        {scanResult ? (
                            <>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={4}>
                                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={600}>Overall Risk</Typography>
                                            <Box sx={{ mt: 1 }}>
                                                <RadialGauge
                                                    value={riskToNumber(scanResult.overall_risk_level)}
                                                    label={scanResult.overall_risk_level}
                                                    color={riskColor(scanResult.overall_risk_level)}
                                                />
                                            </Box>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" fontWeight={600}>Risk Signal Distribution</Typography>
                                            {riskSignals.length > 0 ? (
                                                <DonutChart data={riskSignals} height={220} />
                                            ) : (
                                                <EmptyState message="No risk signals detected." icon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />} />
                                            )}
                                        </Paper>
                                    </Grid>
                                </Grid>
                                <AIAnalysisBox text={scanResult.ai_summary} title="AI Fraud Analysis" />
                                {scanResult.ghost_vendors?.flagged_vendors?.length > 0 && (
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Ghost Vendor Flags</Typography>
                                        <DrillTable
                                            columns={[
                                                { key: 'name', label: 'Vendor' },
                                                { key: 'risk_score', label: 'Score', render: r => <Chip size="small" color={r.risk_score >= 7 ? 'error' : r.risk_score >= 4 ? 'warning' : 'default'} label={r.risk_score} /> },
                                                { key: 'signals', label: 'Signals', render: r => (
                                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                        {(r.signals || []).map((s, j) => <Chip key={j} size="small" variant="outlined" label={s} />)}
                                                    </Stack>
                                                )},
                                                { key: 'action', label: '', render: () => <Button size="small" color="error" variant="outlined" startIcon={<BlockIcon />}>Block</Button> },
                                            ]}
                                            rows={scanResult.ghost_vendors.flagged_vendors}
                                        />
                                    </Paper>
                                )}
                                {scanResult.split_transactions?.splits?.length > 0 && (
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Split Transaction Alerts</Typography>
                                        <DrillTable
                                            columns={[
                                                { key: 'vendor_name', label: 'Vendor' },
                                                { key: 'bill_count', label: 'Bills', align: 'right' },
                                                { key: 'combined_total', label: 'Combined', align: 'right', render: r => fmtMoney(r.combined_total) },
                                                { key: 'threshold', label: 'Threshold', align: 'right', render: r => fmtMoney(r.threshold) },
                                            ]}
                                            rows={scanResult.split_transactions.splits}
                                        />
                                    </Paper>
                                )}
                                {scanResult.benfords?.conforms === false && (
                                    <Paper variant="outlined" sx={{ p: 1.5, borderLeft: '3px solid', borderColor: 'error.main' }}>
                                        <Typography variant="caption" fontWeight={600} color="error.main">
                                            Benford's Law Anomaly — chi-squared: {scanResult.benfords.chi_squared?.toFixed(2)}
                                        </Typography>
                                    </Paper>
                                )}
                            </>
                        ) : (
                            <EmptyState message="Click Run Scan to perform a composite fraud & risk scan." />
                        )}
                    </Stack>
                );
            case 1:
                return ghostVendors ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Ghost Vendor Detector"
                            subtitle="Potentially fictitious vendors flagged by multi-signal analysis"
                            actions={<IconButton size="small" onClick={() => { setGhostVendors(null); run(() => fraudGhostVendors()).then(r => r?.data?.success && setGhostVendors(r.data.data)); }}><RefreshIcon fontSize="small" /></IconButton>}
                        />
                        <SummaryGrid cards={[
                            { label: 'Vendors Scanned', value: ghostVendors.total_vendors_scanned || '—', accent: 'info' },
                            { label: 'Flagged Vendors', value: ghostVendors.flagged_vendors?.length || 0, accent: ghostVendors.flagged_vendors?.length > 0 ? 'warning' : 'success' },
                        ]} cols={2} />
                        {(ghostVendors.flagged_vendors || []).length > 0 ? (
                            <DrillTable
                                columns={[
                                    { key: 'name', label: 'Vendor' },
                                    { key: 'risk_score', label: 'Risk', align: 'right', render: r => <Chip size="small" color={r.risk_score >= 7 ? 'error' : r.risk_score >= 4 ? 'warning' : 'default'} label={`${r.risk_score}/10`} /> },
                                    { key: 'signals', label: 'Signals', render: r => (
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            {(r.signals || []).map((s, j) => <Chip key={j} size="small" variant="outlined" label={s} />)}
                                        </Stack>
                                    )},
                                    { key: 'recommendation', label: 'Recommendation' },
                                    { key: 'action', label: '', render: () => (
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Block"><IconButton size="small" color="error"><BlockIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Investigate"><Button size="small" variant="outlined">Investigate</Button></Tooltip>
                                        </Stack>
                                    )},
                                ]}
                                rows={ghostVendors.flagged_vendors}
                                maxHeight={420}
                            />
                        ) : (
                            <EmptyState message="No ghost vendor indicators found." icon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />} />
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 2:
                return segregation ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Segregation of Duties Monitor" subtitle="Ensure proper separation between financial functions" />
                        <SummaryGrid cards={[
                            { label: 'Total Checks', value: segregation.total_checks || 0, accent: 'info' },
                            { label: 'Violations', value: segregation.violations?.length || 0, accent: segregation.violations?.length > 0 ? 'danger' : 'success' },
                        ]} cols={2} />
                        {(segregation.violations || []).length > 0 ? (
                            <DrillTable
                                columns={[
                                    { key: 'control_type', label: 'Control' },
                                    { key: 'user', label: 'User', render: r => r.user || r.user_id },
                                    { key: 'action', label: 'Action' },
                                    { key: 'details', label: 'Details' },
                                    { key: 'severity', label: 'Severity', render: r => <SeverityBadge level={r.severity} /> },
                                ]}
                                rows={segregation.violations}
                                maxHeight={420}
                            />
                        ) : (
                            <EmptyState message="All segregation-of-duties checks passed." icon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />} />
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 3:
                return violations ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Policy Violations" subtitle="Tracked violations by type & severity" />
                        {violationSeverityData.length > 0 && (
                            <Grid container spacing={1}>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Severity Distribution</Typography>
                                        <DonutChart data={violationSeverityData} height={220} />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Violations by Severity</Typography>
                                        <SimpleBarChart
                                            data={violationSeverityData}
                                            xKey="name"
                                            series={[{ key: 'value', label: 'Count', color: CHART_COLORS.warning }]}
                                            height={220}
                                            showLegend={false}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                        {(violations.violations || []).length > 0 ? (
                            <DrillTable
                                columns={[
                                    { key: 'type', label: 'Type' },
                                    { key: 'severity', label: 'Severity', render: r => <SeverityBadge level={r.severity} /> },
                                    { key: 'count', label: 'Count', align: 'right', render: r => r.count || 1 },
                                    { key: 'details', label: 'Details' },
                                    { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Acknowledge</Button> },
                                ]}
                                rows={violations.violations}
                                maxHeight={420}
                            />
                        ) : (
                            <EmptyState message="No policy violations." icon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />} />
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 4:
                return deadlines ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Filing Deadlines" subtitle="Federal, state, and local filing deadlines with status" />
                        <SummaryGrid cards={[
                            { label: 'Total Deadlines', value: allDeadlines.length, accent: 'info' },
                            { label: 'Overdue', value: deadlines.overdue_count || (deadlines.overdue || []).length, accent: 'danger' },
                            { label: 'Upcoming', value: allDeadlines.filter(d => d.status === 'upcoming').length, accent: 'warning' },
                            { label: 'Filed', value: allDeadlines.filter(d => d.status === 'filed').length, accent: 'success' },
                        ]} />
                        <DrillTable
                            columns={[
                                { key: 'filing_type', label: 'Type', render: r => r.filing_type || r.type || r.name },
                                { key: 'deadline', label: 'Deadline', render: r => r.deadline || r.due_date },
                                { key: 'status', label: 'Status', render: r => (
                                    <Chip size="small"
                                        color={r.status === 'overdue' ? 'error' : r.status === 'filed' ? 'success' : 'info'}
                                        label={r.status || 'upcoming'} />
                                )},
                                { key: 'days', label: 'Days', align: 'right', render: r => r.days_until_due ?? r.days_remaining ?? '' },
                                { key: 'action', label: '', render: r => r.status !== 'filed' && <Button size="small" variant="outlined">File</Button> },
                            ]}
                            rows={allDeadlines}
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 5:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Compliance Gap Scanner"
                            subtitle="Identify gaps in current compliance posture"
                            actions={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <FormControlLabel
                                        control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                        label={<Typography variant="caption">AI Action Plan</Typography>}
                                    />
                                    <Button variant="contained" size="small" startIcon={<PlayArrowIcon />}
                                        onClick={async () => {
                                            const r = await run(() => regComplianceGaps(useAi));
                                            if (r?.data?.success) setGaps(r.data.data);
                                        }}>Scan</Button>
                                </Stack>
                            }
                        />
                        {gaps && (
                            <>
                                <SummaryGrid cards={[
                                    { label: 'Total Gaps', value: gaps.total_gaps || 0, accent: 'warning' },
                                    { label: 'Critical', value: gaps.critical_count || 0, accent: 'danger' },
                                ]} cols={2} />
                                <AIAnalysisBox text={gaps.ai_action_plan || gaps.ai_analysis} title="AI Prioritized Action Plan" />
                                {(gaps.gaps || []).length > 0 && (
                                    <DrillTable
                                        columns={[
                                            { key: 'area', label: 'Area', render: r => r.area || r.category },
                                            { key: 'description', label: 'Gap' },
                                            { key: 'severity', label: 'Severity', render: r => <SeverityBadge level={r.severity} /> },
                                            { key: 'recommended_action', label: 'Action', render: r => r.recommended_action || r.action },
                                        ]}
                                        rows={gaps.gaps}
                                        maxHeight={420}
                                    />
                                )}
                            </>
                        )}
                    </Stack>
                );
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Compliance, Fraud & Risk Center"
            subtitle="Agentic Compliance — fraud detection, SOX controls, policy violations, filing deadlines, compliance gaps."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
